import {
  ClientAddress,
  ClientContact,
  ClientContactPhone,
} from './../data/models/client.model';
import { Client } from '../data/models/client.model';
import { Program, ServicePlan } from '../data/models/client-program.model';
import { phoneUtils } from './phoneUtils';
import omit from 'lodash/omit';
import {
  CreatableNamedLocation,
  EditableNamedLocation,
  NamedLocation,
  Shape,
} from '../data/models/named-location.model';
import { ClientLocation } from '../data/models/client-location.model';
import { Description } from '@mui/icons-material';
import { DateTime } from 'luxon';
import {
  ClientSettingOption,
  ClientSetting,
  ClientSettingsOption,
} from '../data/models/client-settings.model';

class ClientUtils {
  private getDisplayAddress(address?: ClientAddress): any {
    if (address?.addressLine1) {
      const main_text = `${address?.addressLine1}`;
      const secondary_text = `${address?.city}, ${address?.stateCode}, USA`;
      return {
        description: `${main_text}, ${secondary_text}`,
        zipCode: address?.zipCode || '',
      };
    }
    return '';
  }

  public getAddressDefaults(address?: ClientAddress): any {
    const s = {
      ClientAddressUUID: address?.clientAddressUUID || undefined,
      AddressUse: address?.addressUseBC || '',
      Address: this.getDisplayAddress(address),
      AddressStruct: address ? JSON.stringify(address) : undefined,
    };
    return s;
  }

  public getContactDefaults(contact?: ClientContact): any {
    const mobile = phoneUtils.getClientMobilePhone(contact?.phones);
    const landLine = phoneUtils.getClientLandlinePhone(contact?.phones);
    const mobilePhone = mobile?.phoneNumber || '';
    const landlinePhone = landLine?.phoneNumber || '';
    return {
      ClientContactUUID: contact?.clientContactUUID || undefined,
      FirstName: contact?.firstName || '',
      LastName: contact?.lastName || '',
      Title: contact?.title || '',
      Email: contact?.emailAddress || '',
      TimeZone: contact?.timeZone || '',
      Department: contact?.clientContactDepartmentBC || '',
      Role: contact?.clientContactRoleBC || '',
      Mobile: mobilePhone,
      MobileExtension: mobile?.phoneExtension || '',
      MobileIsPrimary: mobile?.isPrimaryPhone || false,
      MobileHasSMSConsent: mobile?.hasSMSConsent || false,
      Landline: landlinePhone,
      LandlineExtension: landLine?.phoneExtension || '',
      LandlineIsPrimary: landLine?.isPrimaryPhone || false,
      LandlineHasSMSConsent: landLine?.hasSMSConsent || false,
    };
  }

  public getPurposeCodeDefaults(): any {
    return {
      Name: '',
    };
  }

  public getServiceAreaDefaults(): any {
    return {
      Name: '',
      ZipCode: '',
      ZipCodeBegin: '',
      ZipCodeEnd: '',
      State: '',
    };
  }

  public getServicePlanDefaults(servicePlan?: ServicePlan): any {
    const cancelMinutes = servicePlan?.lateCancelDeltaMinutes
      ? servicePlan.lateCancelDeltaMinutes * -1
      : undefined;
    return {
      ServicePlanUUID: servicePlan?.servicePlanUUID || undefined,
      Program: servicePlan?.programUUID || '',
      ClientID: servicePlan?.clientServicePlanID || '',
      ProgramUUID: servicePlan?.programUUID || '',
      PlanName: servicePlan?.clientServicePlanName || '',
      DisplayName: servicePlan?.name || '',
      LateCancelDeltaMinutes: cancelMinutes || '120',
      BillableStatuses: servicePlan?.billableTripLegStatuses || [],
      ReimbursableStatuses: servicePlan?.reimbursableTripLegStatuses || [],
    };
  }

  public getProgramDefaults(program?: Program): any {
    return {
      ProgramUUID: program?.programUUID || undefined,
      Name: program?.name || '',
      ProgramID: program?.clientProgramID || '',
      ProgramBC: program?.clientBC || '',
      Description: program?.description || '',
    };
  }

  public getProfileDefaults(client?: Client): any {
    const clientData = {
      ClientBC: client?.clientBC || '',
      ParentOrganization: client?.parentOrganizationBC || '',
      ClientLegalName: client?.legalName || '',
      Abbreviation: client?.clientAbbreviation || '',
      Vertical: client?.clientVerticalBC || '',
      isInternal: client?.isInternal || false,
      effectiveStartDate: client?.effectiveStartDate
        ? DateTime.fromISO(client.effectiveStartDate)
        : null,
      effectiveEndDate: client?.effectiveEndDate
        ? DateTime.fromISO(client.effectiveEndDate)
        : null,
    };
    return clientData;
  }

  public getDefaults(): any {
    const clientData = {
      ParentOrganization: '',
      ClientBC: '',
      ClientLegalName: '',
      Abbreviation: '',
      Vertical: '',
      isInternal: false,
      EffectiveStartDate: null,
      EffectiveEndDate: null,
    };
    return clientData;
  }

  public getClientFromFormData(formData: any, isNew: boolean): Client {
    const client: Client = {
      clientBC: formData.ClientBC,
      parentOrganizationBC: isNew ? formData.ParentOrganization : undefined,
      clientVerticalBC: formData.Vertical,
      clientAbbreviation: formData.Abbreviation,
      legalName: formData.ClientLegalName,
      isInternal: formData.isInternal,
      effectiveStartDate: formData.EffectiveStartDate?.toISODate(),
      effectiveEndDate: formData.EffectiveEndDate?.toISODate(),
    };

    return client;
  }

  public getAddressFromFormData(
    formData: any,
    currentState?: string,
  ): ClientAddress {
    const addr = JSON.parse(formData.AddressStruct || '{}');
    addr['clientAddressUUID'] = formData.ClientAddressUUID;
    addr['addressUseBC'] = formData.AddressUse;

    return omit(
      addr,
      'state',
      'modifiedByUserID',
      'modifiedDateTimeUTC',
      'correlationUUID',
    ) as ClientAddress;
  }

  private getPhone(
    formData: any,
    phType: string,
  ): ClientContactPhone | undefined {
    let phone: ClientContactPhone | undefined = undefined;
    if (formData[phType]) {
      const phoneNumber = formData[phType];
      phone = {
        phoneNumber: `${phoneNumber.replace(/\s/g, '')}`,
        phoneExtension: formData[`${phType}Extension`],
        isPrimaryPhone: formData[`${phType}IsPrimary`],
        hasSMSConsent: formData[`${phType}HasSMSConsent`],
        phoneNumberTypeBC: phType.toUpperCase(),
      };
    }
    return phone;
  }

  public getContactFromFormData(
    formData: any,
    currentState?: string,
  ): ClientContact {
    const phones: ClientContactPhone[] = [];
    const mobile = this.getPhone(formData, 'Mobile');
    const land = this.getPhone(formData, 'Landline');
    if (mobile) {
      phones.push(mobile);
    }
    if (land) {
      phones.push(land);
    }
    return {
      clientContactUUID: formData.ClientContactUUID,
      firstName: formData.FirstName,
      lastName: formData.LastName,
      title: formData.Title,
      emailAddress: formData.Email,
      timeZone: formData.TimeZone,
      clientContactDepartmentBC: formData.Department,
      clientContactRoleBC: formData.Role,
      phones,
    };
  }

  public getProgramFromFormData(clientBC: string, formData: any): Program {
    return {
      clientBC,
      programUUID: formData.ProgramUUID,
      clientProgramID: formData.ProgramID,
      name: formData.Name,
      description: formData.Description,
    };
  }

  public getServicePlanFromFormData(
    clientBC: string,
    formData: any,
    forUpdate: boolean,
  ): ServicePlan {
    const cancelMinutes = formData.LateCancelDeltaMinutes
      ? parseInt(formData.LateCancelDeltaMinutes) * -1
      : undefined;
    return {
      clientBC,
      programUUID: forUpdate ? undefined : formData.Program,
      servicePlanUUID: formData.ServicePlanUUID,
      clientServicePlanID: formData.ClientID,
      clientServicePlanName: formData.PlanName,
      name: formData.DisplayName,
      effectiveStartDate: formData.Start?.toISODate(),
      effectiveEndDate: formData.End?.toISODate(),
      lateCancelDeltaMinutes: cancelMinutes || undefined,
      billableTripLegStatuses: formData.BillableStatuses,
      reimbursableTripLegStatuses: formData.ReimbursableStatuses,
    };
  }

  public getCreatableNamedAddressFromFormData(
    formData: any,
  ): CreatableNamedLocation {
    const address = JSON.parse(formData.addressStruct || '{}');
    return {
      addressLine1: address.addressLine1,
      addressLine2: address.addressLine2,
      city: address.city,
      stateCode: address.stateCode,
      county: address.county,
      zipCode: address.zipCode,
      latitude: address.latitude,
      longitude: address.longitude,
      placeID: address.placeID,
      timeZone: address.timeZone,
      name: formData.name,
      reason: formData.reason,
      boundary: {
        shape: formData.boundary.shape,
        radius: parseInt(formData.boundary.radius),
      },
    };
  }

  public getEditableNamedAddressFromFormData(
    namedLocation: NamedLocation,
    formData: any,
  ): EditableNamedLocation {
    return {
      namedLocationUUID: namedLocation.namedLocationUUID,
      ...this.getCreatableNamedAddressFromFormData(formData),
    };
  }

  public getNamedLocationDefaults(namedLocation?: NamedLocation): any {
    let address: any = '';
    if (namedLocation?.addressLine1) {
      const main_text = `${namedLocation?.addressLine1}`;
      const secondary_text = `${namedLocation?.city}, ${namedLocation?.stateCode}, USA`;
      address = {
        description: `${main_text}, ${secondary_text}`,
        zipCode: namedLocation?.zipCode || '',
        place_id: namedLocation?.placeID || '',
      };
    }

    return {
      address,
      addressStruct: namedLocation
        ? JSON.stringify({
            addressLine1: namedLocation.addressLine1,
            addressLine2: namedLocation.addressLine2,
            city: namedLocation.city,
            stateCode: namedLocation.stateCode,
            county: namedLocation.county,
            zipCode: namedLocation.zipCode,
            latitude: namedLocation.latitude,
            longitude: namedLocation.longitude,
            placeID: namedLocation.placeID,
            timeZone: namedLocation.timeZone,
          })
        : undefined,
      name: namedLocation?.name || '',
      reason: namedLocation?.reason || '',
      boundary: namedLocation?.boundary || {
        shape: Shape.circle,
        radius: 1,
      },
    };
  }

  public getClientLocationDefaults(
    clientLocation?: ClientLocation,
    namedLocation?: NamedLocation,
  ): any {
    return {
      description: clientLocation?.description || '',
      forUseAs: clientLocation?.forUseAs || '',
      ...this.getNamedLocationDefaults(namedLocation),
    };
  }

  public checkAddress(address: any, keys: string[]): boolean {
    if (address) {
      for (const key of keys) {
        if (!address[key]) {
          return false;
        }
      }
      return true;
    }
    return false;
  }

  public isAddressFullyPopulated(address: any): boolean {
    return this.checkAddress(address, [
      'addressLine1',
      'city',
      'stateCode',
      'zipCode',
      'latitude',
      'longitude',
    ]);
  }

  public getPickUpTimeOffsetMinutes(
    clientSettingOptions: ClientSettingOption[],
    clientSettings: ClientSetting[] | null,
  ) {
    const optionBC = clientSettingOptions.find(
      (o) =>
        o.clientSettingOptionBC ===
        ClientSettingsOption.PickupTimeOffsetMinutes,
    )?.clientSettingOptionBC;
    const pref = clientSettings?.find(
      (p) => p.clientSettingOptionBC === optionBC,
    );
    return pref?.clientSettingValue || 60;
  }

  public getPickupTimeOffsetSettingsFromData(
    clientSettingOptions: ClientSettingOption[],
    data: any,
    clientSettingID?: number,
  ): ClientSetting {
    const optId =
      clientSettingOptions.find(
        (cso) =>
          cso.clientSettingOptionBC ===
          ClientSettingsOption.PickupTimeOffsetMinutes,
      )?.clientSettingOptionID || 0;
    return {
      clientSettingID,
      clientSettingOptionID: optId,
      clientSettingValue: parseInt(data.DefaultPickup, 10),
      effectiveStartDate: DateTime.now().toISODate(),
      effectiveEndDate: null,
      clientSettingState: 'active',
      clientSettingOptionBC: ClientSettingsOption.PickupTimeOffsetMinutes,
    };
  }
}

const clientUtils = new ClientUtils();

export default clientUtils;
